import React from 'react';
import {
  type ActivityListRow
} from 'coop-adventure-engine';
import type {
  RichTextDescription,
  RichTextElement
} from 'coop-adventure-engine';
import { plainTextToRichText, isRichTextDescription } from '../../utils/richTextRenderer';
import { generateImageUrl } from '../../utils/imageUtils';
import IconImage from './IconImage.tsx';
import InlineOption from '../ui/InlineOption';
import InventoryList from '../ui/InventoryList';
import SelectableInventoryList from '../ui/SelectableInventoryList';
import FriendRequestList from '../ui/FriendRequestList';
import FriendsList from '../ui/FriendsList';
import AdventureInviteList from '../ui/AdventureInviteList';
import AdventureInviteStatusList from '../ui/AdventureInviteStatusList';
import GuestAdventureList from '../ui/GuestAdventureList';
import KeyedList from '../ui/KeyedList';
import AdventureList from './AdventureList';
import AdventureTitleImage from './AdventureTitleImage';
import AdventureTitleImageFromId from './AdventureTitleImageFromId';
import ActiveAdventureList from './ActiveAdventureList';
import ActiveAdventureTitleImage from './ActiveAdventureTitleImage';
import PortraitImage from './PortraitImage';
import LocationWithPortraits from './LocationWithPortraits';
import ActivityList from './ActivityList';
import UserInputField from './UserInputField';
import VerificationCodeInput from './VerificationCodeInput';
import SpinnerField from './SpinnerField';
import TimePickerField from './TimePickerField';
import DatePickerField from './DatePickerField';
import ColorPickerField from './ColorPickerField';
import ColorSampleBlock from './ColorSampleBlock';
import CharacterComment from './CharacterComment';
import FeedbackCard from './FeedbackCard';
import ScrollArea from './ScrollArea';
import SharedData from './SharedData';
import { interpolateVariableOverrides } from '../../utils/interpolateSharedVariables';
import ScheduleEventRow from './ScheduleEventRow';
import JoinEventCard from './JoinEventCard';
import ScheduleEventAttendance from './ScheduleEventAttendance';
import SyncOption from './SyncOption';
import TimerDisplay from './TimerDisplay';
import TimerAudio from './TimerAudio';
import CodeDisplay from './CodeDisplay';
import { formatFriendlyDateTime } from '../../utils/helpers';
import Button from './Button';

const PORTRAIT_VARIANTS = ['thumbnail', 'card', 'large'] as const;
const ICON_VARIANTS = ['small', 'large'] as const;
const TITLE_IMAGE_VARIANTS = ['thumbnail', 'card', 'large'] as const;
const DEFAULT_PORTRAIT_VARIANT = 'card';

const isPortraitVariant = (value: string): value is typeof PORTRAIT_VARIANTS[number] =>
  PORTRAIT_VARIANTS.includes(value as typeof PORTRAIT_VARIANTS[number]);

const isIconVariant = (value: string): value is typeof ICON_VARIANTS[number] =>
  ICON_VARIANTS.includes(value as typeof ICON_VARIANTS[number]);

const isTitleImageVariant = (value: string): value is typeof TITLE_IMAGE_VARIANTS[number] =>
  TITLE_IMAGE_VARIANTS.includes(value as typeof TITLE_IMAGE_VARIANTS[number]);

export interface ActivityListRendererContext {
  /** Activity list click handler; rows are computed server-side. Navigates to the row start scene only (no implicit activity state). */
  onNavigate: (startSceneId: string) => void;
  /**
   * When `<Activity-List>` uses `selectionTarget` + `selectionVariable`, clicks call this instead of {@link onNavigate}.
   */
  onActivityReferenceSelect?: ((activityId: string, targetScene: string, variableName: string) => void) | undefined;
  /**
   * When the embed sets `redo_scene=`, completed rows show REDO which calls this with (redo scene id, activity id).
   */
  onActivityRedo?: ((redoSceneId: string, activityId: string) => void) | undefined;
  disableInteractions?: boolean | undefined;
}

export interface RichTextRendererProps {
  description: string | RichTextDescription;
  onInlineOptionClick?: ((optionId: string) => void) | undefined;
  onInventoryItemSelect?: ((itemTag: string, targetScene: string) => void) | undefined;
  onColorOptionSelect?: ((variableName: string, colorValue: string, targetScene: string) => void) | undefined;
  onFriendRequestSelect?: ((requestId: string, targetScene: string) => void) | undefined;
  onFriendSelect?: ((friendId: string, targetScene?: string) => void) | undefined;
  onScheduleEventSelect?: ((eventId: string, targetScene: string) => void) | undefined;
  onScheduleEventTimeSelect?: ((eventId: string, timeEpochSeconds: number, targetScene: string, realWorldLocation?: string) => void) | undefined;
  onJoinEventSelect?: ((eventId: string, startScene: string) => void) | undefined;
  onFeedbackCardSelect?: ((feedbackId: string, targetScene: string) => void) | undefined;
  onSyncOptionSelect?: ((syncVar: string, targetScene: string, characterNames?: string[], targetByCharacter?: Record<string, string>, syncEffects?: string) => void) | undefined;
  onAdventureInviteClick?: ((inviteId: string) => void) | undefined;
  onAdventureInviteStatusSelect?: ((inviteId: string, targetScene: string) => void) | undefined;
  onGuestAdventureSelect?: ((inviteId: string, targetScene: string, variableName: string) => void) | undefined;
  onKeyedListEntrySelect?: ((entryId: string, targetScene: string, sourceVariable: string, variableName: string) => void) | undefined;
  /** User IDs to exclude from Friends-List (e.g. players already in the session). Used when excludeInSession=true. */
  excludeUserIds?: string[] | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
  customColor?: string | undefined; // Optional custom button color for inline options
  disableInlineOptions?: boolean | undefined; // When true (e.g. action pending), inline options are disabled
  /** When set, `<Activity-List>` embeds render from session adventure + variables. */
  activityListContext?: ActivityListRendererContext | undefined;
  /** Session id for `<Shared-Data>` batch queries. */
  sessionId?: string | undefined;
  /** Local variable overrides for `${name}` interpolation (e.g. shared_* inside Shared-Data blocks). */
  variableOverrides?: Record<string, string | number> | undefined;
}

/**
 * Renders a RichTextDescription as React components
 * This is needed for inline options which require React components
 */
export const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  description,
  onInlineOptionClick,
  onInventoryItemSelect,
  onColorOptionSelect,
  onFriendRequestSelect,
  onFriendSelect,
  onScheduleEventSelect,
  onScheduleEventTimeSelect,
  onJoinEventSelect,
  onFeedbackCardSelect,
  onSyncOptionSelect,
  onAdventureInviteClick,
  onAdventureInviteStatusSelect,
  onGuestAdventureSelect,
  onKeyedListEntrySelect,
  excludeUserIds,
  className = '',
  style,
  customColor,
  disableInlineOptions = false,
  activityListContext,
  sessionId,
  variableOverrides,
}) => {
  const getColorWithAlpha = (color: string, alpha: string) => `${color}${alpha}`;

  const nestedRendererProps: Omit<RichTextRendererProps, 'description' | 'variableOverrides'> = {
    onInlineOptionClick,
    onInventoryItemSelect,
    onColorOptionSelect,
    onFriendRequestSelect,
    onFriendSelect,
    onScheduleEventSelect,
    onScheduleEventTimeSelect,
    onJoinEventSelect,
    onFeedbackCardSelect,
    onSyncOptionSelect,
    onAdventureInviteClick,
    onAdventureInviteStatusSelect,
    onGuestAdventureSelect,
    onKeyedListEntrySelect,
    excludeUserIds,
    className,
    style,
    customColor,
    disableInlineOptions,
    activityListContext,
    sessionId,
  };

  const renderElement = (element: RichTextElement, index: number): React.ReactNode => {
    if (!element || typeof element.type !== 'string') {
      return null;
    }

    const key = `element-${index}`;

    switch (element.type) {
      case 'paragraph': {
        const paragraphContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : (
            <SafeText
              text={
                variableOverrides
                  ? interpolateVariableOverrides(element.content as string, variableOverrides)
                  : (element.content as string)
              }
            />
          );
        // Center paragraphs that contain only portrait images (e.g. from getCharacterPortraitsInScene)
        // This includes: direct portrait images, or a single script element whose output is only portraits
        const content = element.content;
        const directPortraitsOnly = Array.isArray(content) && content.length > 0 &&
          content.every((child) => child?.type === 'image' && child?.imageType === 'portrait');
        const scriptWithPortraitsOnly = Array.isArray(content) && content.length === 1 &&
          content[0]?.type === 'script' && Array.isArray((content[0] as any).content) &&
          ((content[0] as any).content as RichTextElement[]).length > 0 &&
          ((content[0] as any).content as RichTextElement[]).every(
            (c) => c?.type === 'image' && c?.imageType === 'portrait'
          );
        const directScheduleEventsOnly = Array.isArray(content) && content.length > 0 &&
          content.every((child) => child?.type === 'schedule-event');
        const scriptWithScheduleEventsOnly = Array.isArray(content) && content.length === 1 &&
          content[0]?.type === 'script' && Array.isArray((content[0] as any).content) &&
          ((content[0] as any).content as RichTextElement[]).length > 0 &&
          ((content[0] as any).content as RichTextElement[]).every(
            (c) => c?.type === 'schedule-event'
          );
        const directJoinEventCardsOnly = Array.isArray(content) && content.length > 0 &&
          content.every((child) => child?.type === 'join-event-card');
        const scriptWithJoinEventCardsOnly = Array.isArray(content) && content.length === 1 &&
          content[0]?.type === 'script' && Array.isArray((content[0] as any).content) &&
          ((content[0] as any).content as RichTextElement[]).length > 0 &&
          ((content[0] as any).content as RichTextElement[]).every(
            (c) => c?.type === 'join-event-card'
          );
        const onlyPortraits = directPortraitsOnly || scriptWithPortraitsOnly;
        const onlyScheduleEvents = directScheduleEventsOnly || scriptWithScheduleEventsOnly;
        const onlyJoinEventCards = directJoinEventCardsOnly || scriptWithJoinEventCardsOnly;
        const onlyFeedbackCards = Array.isArray(content) && content.length > 0 &&
          content.every((child) => child?.type === 'feedback-card');
        const extractAnalyticsRows = (nodes: RichTextElement[]): RichTextElement[] | null => {
          const rows: RichTextElement[] = [];
          const walk = (node: RichTextElement): boolean => {
            if (!node) {
              return true;
            }
            if (node.type === 'analytics-table-row') {
              rows.push(node);
              return true;
            }
            if (node.type === 'text') {
              return typeof node.content === 'string' ? node.content.trim().length === 0 : true;
            }
            if ((node.type === 'script' || node.type === 'paragraph') && Array.isArray((node as any).content)) {
              return ((node as any).content as RichTextElement[]).every(walk);
            }
            return false;
          };
          const ok = nodes.length > 0 && nodes.every(walk);
          return ok && rows.length > 0 ? rows : null;
        };
        const analyticsRows = Array.isArray(content) ? extractAnalyticsRows(content as RichTextElement[]) : null;
        const paragraphClass = onlyPortraits
          ? 'rich-text-paragraph rich-text-portrait-row'
          : 'rich-text-paragraph';
        const contentHasAnalyticsRows = Array.isArray(content) &&
          content.some((child) => child?.type === 'analytics-table-row');
        if (onlyScheduleEvents) {
          return (
            <div key={key} className={`${paragraphClass} rich-text-schedule-events`}>
              {paragraphContent}
            </div>
          );
        }
        if (onlyJoinEventCards) {
          return (
            <div key={key} className={`${paragraphClass} rich-text-join-event-cards`}>
              {paragraphContent}
            </div>
          );
        }
        if (onlyFeedbackCards) {
          return (
            <div key={key} className={`${paragraphClass} rich-text-feedback-cards`}>
              {paragraphContent}
            </div>
          );
        }
        if (analyticsRows) {
          return (
            <div key={key} className={`${paragraphClass} rich-text-analytics-table`}>
              {analyticsRows.map((row, rowIndex) => renderElement(row, rowIndex))}
            </div>
          );
        }
        if (contentHasAnalyticsRows && Array.isArray(content)) {
          const segmentedContent: React.ReactNode[] = [];
          let analyticsGroup: RichTextElement[] = [];
          const flushAnalyticsGroup = (groupIndex: number) => {
            if (analyticsGroup.length > 0) {
              segmentedContent.push(
                <div key={`analytics-group-${groupIndex}`} className="rich-text-analytics-table">
                  {analyticsGroup.map((row, rowIndex) => renderElement(row, rowIndex))}
                </div>
              );
              analyticsGroup = [];
            }
          };

          content.forEach((child, childIndex) => {
            if (child?.type === 'analytics-table-row') {
              analyticsGroup.push(child);
              return;
            }
            flushAnalyticsGroup(childIndex);
            segmentedContent.push(renderElement(child, childIndex));
          });
          flushAnalyticsGroup(content.length);

          return (
            <div key={key} className={paragraphClass}>
              {segmentedContent}
            </div>
          );
        }
        const hasNestedParagraph =
          Array.isArray(content) && content.some((child) => child?.type === 'paragraph');
        if (hasNestedParagraph) {
          return (
            <div key={key} className={paragraphClass}>
              {paragraphContent}
            </div>
          );
        }
        return (
          <p key={key} className={paragraphClass}>
            {paragraphContent}
          </p>
        );
      }

      case 'heading':
        const headingContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText text={element.content as string} />;
        const level = element.level || 1;
        const HeadingTag = `h${Math.min(Math.max(level, 1), 3)}` as keyof React.JSX.IntrinsicElements;
        return (
          <HeadingTag key={key} className={`rich-text-heading rich-text-heading-${level}`}>
            {headingContent}
          </HeadingTag>
        );

      case 'list':
        const listItems = Array.isArray(element.content) 
          ? element.content.map((item, itemIndex) => {
              if (!item) {
                return null;
              }
              // Handle both string content and array of rich text elements
              const itemContent = Array.isArray(item.content) 
                ? item.content.map((child, childIndex) => renderElement(child, childIndex))
                : <SafeText text={item.content as string} />;
              return (
                <li key={`item-${itemIndex}`} className="rich-text-list-item">
                  {itemContent}
                </li>
              );
            })
          : [];
        
        const ListTag = element.listType === 'numbered' ? 'ol' : 'ul';
        const listClass = `rich-text-list rich-text-list-${element.listType}`;
        return (
          <ListTag key={key} className={listClass}>
            {listItems}
          </ListTag>
        );

      case 'bold':
        const boldContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText text={element.content as string} />;
        return (
          <strong key={key} className="rich-text-bold">
            {boldContent}
          </strong>
        );

      case 'italic':
        const italicContent = Array.isArray(element.content) 
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText text={element.content as string} />;
        return (
          <em key={key} className="rich-text-italic">
            {italicContent}
          </em>
        );

      case 'text': {
        const rawText = element.content as string;
        const text = variableOverrides
          ? interpolateVariableOverrides(rawText, variableOverrides)
          : rawText;
        return <SafeText key={key} text={text} />;
      }

      case 'image':
        if (element.imageId && element.imageType) {
          const altText = element.imageId.replace(/_/g, ' ').replace(/:/g, ' - ');

          if (element.imageType === 'icon') {
            const iconVariant =
              element.imageVariant && typeof element.imageVariant === 'string' && isIconVariant(element.imageVariant)
                ? element.imageVariant
                : 'small';

            return (
              <IconImage
                key={key}
                imageId={element.imageId}
                variant={iconVariant}
                alt={altText}
              />
            );
          }

          if (element.imageType === 'portrait') {
            const portraitVariant =
              element.imageVariant && typeof element.imageVariant === 'string' && isPortraitVariant(element.imageVariant)
                ? element.imageVariant
                : DEFAULT_PORTRAIT_VARIANT;

            return (
              <PortraitImage
                key={key}
                portraitId={element.imageId}
                variant={portraitVariant}
                alt={altText}
              />
            );
          }

          if (element.imageType === 'title-image') {
            const titleVariant =
              element.imageVariant && typeof element.imageVariant === 'string' && isTitleImageVariant(element.imageVariant)
                ? element.imageVariant
                : 'card';

            return (
              <AdventureTitleImage
                key={key}
                titleImageId={element.imageId}
                variant={titleVariant}
                alt={altText}
                className="rich-text-image rich-text-title-image"
                responsive={true}
                lazy={false}
              />
            );
          }

          // Fallback for banner and location-image (plain img)
          const fallbackVariant =
            element.imageVariant && typeof element.imageVariant === 'string'
              ? element.imageVariant
              : 'card';
          const imageUrl = generateImageUrl(element.imageId, element.imageType, fallbackVariant);
          let imageClass = 'rich-text-image';
          if (element.imageType === 'banner') {
            imageClass += ' rich-text-banner';
          }
          if (element.imageType === 'location-image') {
            imageClass += ' rich-text-location-image';
          }
          return (
            <img
              key={key}
              src={imageUrl}
              alt={altText}
              className={imageClass}
            />
          );
        }
        return null;

      case 'inline-option':
        if (!onInlineOptionClick) {
          console.warn('RichTextRenderer: onInlineOptionClick handler not provided for inline option');
          return null;
        }
        return (
          <InlineOption
            key={key}
            element={element}
            onOptionClick={onInlineOptionClick}
            disabled={disableInlineOptions}
            {...(customColor ? { customColor } : {})}
          />
        );

      case 'inventory-list':
        // Handle both old format (inventoryType/inventoryOwner) and new format (attributes)
        let characterName: string | undefined;
        let locationTag: string | undefined;
        
        if (element.inventoryType && element.inventoryOwner) {
          // Old format from parser
          if (element.inventoryType === 'character') {
            characterName = element.inventoryOwner;
          } else if (element.inventoryType === 'location') {
            locationTag = element.inventoryOwner;
          }
        } else {
          // New format with attributes
          const attributes = (element as any).attributes || {};
          characterName = attributes.character;
          locationTag = attributes.location;
        }
        
        if (!characterName && !locationTag) {
          console.warn('RichTextRenderer: inventory-list element missing character or location attribute');
          return null;
        }

        // Check if this is a selectable inventory list
        if (element.selectionTarget) {
          if (!onInventoryItemSelect) {
            console.warn('RichTextRenderer: onInventoryItemSelect handler not provided for selectable inventory list');
            return null;
          }
          
          return (
            <SelectableInventoryList
              key={key}
              {...(characterName ? { characterName } : {})}
              {...(locationTag ? { locationTag } : {})}
              selectionTarget={element.selectionTarget}
              onItemSelect={onInventoryItemSelect}
              className="rich-text-inventory-list"
            />
          );
        }
        
        // Regular (non-selectable) inventory list
        return (
          <InventoryList
            key={key}
            {...(characterName ? { characterName } : {})}
            {...(locationTag ? { locationTag } : {})}
            className="rich-text-inventory-list"
          />
        );

      case 'adventure-list':
        // Handle adventure-list tag: <Adventure-List> or <Adventure-List tags=event-host>
        return (
          <AdventureList
            key={key}
            excludeSystemAdventures={true}
            className="rich-text-adventure-list"
            {...(element.adventureListTags ? { tags: element.adventureListTags } : {})}
            {...(customColor ? { customColor } : {})}
          />
        );

      case 'adventure':
        if (!element.adventureId) {
          console.warn('RichTextRenderer: adventure element missing adventureId');
          return null;
        }
        return (
          <AdventureTitleImageFromId
            key={key}
            adventureId={element.adventureId}
            variant="card"
            alt={`Adventure: ${element.adventureId}`}
            className="rich-text-image rich-text-adventure"
          />
        );

      case 'active-adventure':
        // Handle active-adventure tag: <active-adventure:session-id>
        if (!element.sessionId) {
          console.warn('RichTextRenderer: active-adventure element missing sessionId');
          return null;
        }
        return (
          <ActiveAdventureTitleImage
            key={key}
            sessionId={element.sessionId}
            variant="card"
            alt={`Active Adventure: ${element.sessionId}`}
            className="rich-text-image rich-text-active-adventure"
          />
        );

      case 'active-adventure-list':
        // Handle active-adventure-list tag: <Active-Adventure-List> or <Active-Adventure-List tags=event-host>
        return (
          <ActiveAdventureList
            key={key}
            excludeSystemAdventures={true}
            className="rich-text-active-adventure-list"
            {...(element.adventureListTags ? { tags: element.adventureListTags } : {})}
            {...(customColor ? { customColor } : {})}
          />
        );

      case 'friend-requests':
        // Handle friend-requests tag: <Friend-Requests selectionTarget=scene_id>
        if (element.selectionTarget) {
          if (!onFriendRequestSelect) {
            console.warn('RichTextRenderer: onFriendRequestSelect handler not provided for selectable friend requests');
            return null;
          }
          
          return (
            <FriendRequestList
              key={key}
              selectionTarget={element.selectionTarget}
              onRequestSelect={onFriendRequestSelect}
              className="rich-text-friend-requests"
              {...(customColor && { customColor })}
            />
          );
        }
        
        // Regular (non-selectable) friend requests list
        return (
          <FriendRequestList
            key={key}
            className="rich-text-friend-requests"
          />
        );

      case 'friends-list':
        // Handle friends-list tag: <Friends-List selectionTarget=scene_id excludeInSession=true>
        if (element.selectionTarget) {
          if (!onFriendSelect) {
            console.warn('RichTextRenderer: onFriendSelect handler not provided for selectable friends list');
            return null;
          }
          
          return (
            <FriendsList
              key={key}
              selectionTarget={element.selectionTarget}
              onFriendSelect={onFriendSelect}
              excludeUserIds={element.excludeInSession ? excludeUserIds : undefined}
              className="rich-text-friends-list"
              {...(customColor && { customColor })}
            />
          );
        }
        
        // Regular (non-selectable) friends list
        return (
          <FriendsList
            key={key}
            excludeUserIds={element.excludeInSession ? excludeUserIds : undefined}
            className="rich-text-friends-list"
          />
        );

      case 'adventure-invite-list':
        // Handle adventure-invite-list tag: <Adventure-Invite-List>
        // All adventure invites are clickable and will join the adventure
        return (
          <AdventureInviteList
            key={key}
            {...(onAdventureInviteClick && { onInviteClick: onAdventureInviteClick })}
            className="rich-text-adventure-invite-list"
            {...(customColor && { customColor })}
          />
        );

      case 'adventure-invite-status-list':
        // Handle adventure-invite-status-list tag: <Adventure-Invite-Status-List selectionTarget=scene_id>
        if (element.selectionTarget) {
          if (!onAdventureInviteStatusSelect) {
            console.warn('RichTextRenderer: onAdventureInviteStatusSelect handler not provided for selectable adventure invite status list');
            return null;
          }
          
          return (
            <AdventureInviteStatusList
              key={key}
              onInviteClick={(inviteId) => onAdventureInviteStatusSelect(inviteId, element.selectionTarget!)}
              className="rich-text-adventure-invite-status-list"
              {...(customColor && { customColor })}
            />
          );
        }
        
        // Regular (non-selectable) adventure invite status list
        return (
          <AdventureInviteStatusList
            key={key}
            className="rich-text-adventure-invite-status-list"
          />
        );

      case 'guest-adventure-list':
        if (element.selectionTarget) {
          if (!onGuestAdventureSelect) {
            console.warn('RichTextRenderer: onGuestAdventureSelect handler not provided for selectable guest adventure list');
            return null;
          }

          const guestSelectionVariable = element.keyedListSelectionVariable ?? 'selected_guest_id';
          return (
            <GuestAdventureList
              key={key}
              {...(sessionId ? { sessionId } : {})}
              onInviteClick={(inviteId) =>
                onGuestAdventureSelect(inviteId, element.selectionTarget!, guestSelectionVariable)
              }
              {...(element.guestAdventureListStatusVariable
                ? { statusVariable: element.guestAdventureListStatusVariable }
                : {})}
              {...(element.guestAdventureListStatusLabels
                ? { statusLabels: element.guestAdventureListStatusLabels }
                : {})}
              {...(element.guestAdventureListStatusFilter
                ? { statusFilter: element.guestAdventureListStatusFilter }
                : {})}
              className="rich-text-guest-adventure-list"
              {...(customColor && { customColor })}
            />
          );
        }

        return (
          <GuestAdventureList
            key={key}
            {...(sessionId ? { sessionId } : {})}
            {...(element.guestAdventureListStatusVariable
              ? { statusVariable: element.guestAdventureListStatusVariable }
              : {})}
            {...(element.guestAdventureListStatusLabels
              ? { statusLabels: element.guestAdventureListStatusLabels }
              : {})}
            {...(element.guestAdventureListStatusFilter
              ? { statusFilter: element.guestAdventureListStatusFilter }
              : {})}
            className="rich-text-guest-adventure-list"
          />
        );

      case 'keyed-list':
        if (!element.keyedListSourceVariable) {
          console.warn('RichTextRenderer: keyed-list element missing sourceVariable attribute');
          return null;
        }

        if (element.selectionTarget) {
          if (!onKeyedListEntrySelect) {
            console.warn('RichTextRenderer: onKeyedListEntrySelect handler not provided for selectable keyed list');
            return null;
          }

          const keyedSelectionVariable = element.keyedListSelectionVariable ?? 'selected_id';
          return (
            <KeyedList
              key={key}
              sourceVariable={element.keyedListSourceVariable}
              selectionVariable={keyedSelectionVariable}
              onEntrySelect={(entryId) =>
                onKeyedListEntrySelect(
                  entryId,
                  element.selectionTarget!,
                  element.keyedListSourceVariable!,
                  keyedSelectionVariable
                )
              }
              className="rich-text-keyed-list"
              {...(customColor && { customColor })}
            />
          );
        }

        return (
          <KeyedList
            key={key}
            sourceVariable={element.keyedListSourceVariable}
            className="rich-text-keyed-list"
          />
        );

      case 'user-input':
        if (!element.userInputAttributes?.variable) {
          console.warn('RichTextRenderer: user-input element missing variable attribute');
          return null;
        }
        return (
          <UserInputField
            key={key}
            variableName={element.userInputAttributes.variable}
            {...(element.userInputAttributes.placeholder && { placeholder: element.userInputAttributes.placeholder })}
            {...(element.userInputAttributes.maxLength !== undefined && { maxLength: element.userInputAttributes.maxLength })}
            {...(element.userInputAttributes.autoSave !== undefined && { autoSave: element.userInputAttributes.autoSave })}
            {...(element.userInputAttributes.debounce !== undefined && { debounce: element.userInputAttributes.debounce })}
            {...(element.userInputAttributes.confirmationTarget && { confirmationTarget: element.userInputAttributes.confirmationTarget })}
            {...(element.userInputAttributes.type && { type: element.userInputAttributes.type })}
            {...(element.userInputAttributes.multiline !== undefined && { multiline: element.userInputAttributes.multiline })}
            {...(element.userInputAttributes.rows !== undefined && { rows: element.userInputAttributes.rows })}
            className="rich-text-user-input-element"
          />
        );

      case 'verification-code-input':
        if (!element.verificationCodeInputAttributes?.variable) {
          console.warn('RichTextRenderer: verification-code-input element missing variable attribute');
          return null;
        }
        return (
          <VerificationCodeInput
            key={key}
            variableName={element.verificationCodeInputAttributes.variable}
            {...(element.verificationCodeInputAttributes.placeholder && { placeholder: element.verificationCodeInputAttributes.placeholder })}
            className="rich-text-verification-code-input-element"
          />
        );

      case 'spinner':
        if (!element.spinnerAttributes?.variable || !element.spinnerAttributes?.values) {
          console.warn('RichTextRenderer: spinner element missing variable or values attribute');
          return null;
        }
        return (
          <SpinnerField
            key={key}
            variableName={element.spinnerAttributes.variable}
            values={element.spinnerAttributes.values.split(',').map((v) => v.trim()).filter(Boolean)}
            {...(element.spinnerAttributes.default && { defaultValue: element.spinnerAttributes.default })}
            {...(element.spinnerAttributes.order && { order: element.spinnerAttributes.order })}
            className="rich-text-spinner-element"
          />
        );

      case 'color-picker': {
        const cp = element.colorPickerAttributes;
        if (!cp?.variable || !cp.colors || !cp.selectionTarget) {
          console.warn('RichTextRenderer: color-picker element missing variable, colors, or selectionTarget');
          return null;
        }
        if (!onColorOptionSelect) {
          console.warn('RichTextRenderer: onColorOptionSelect handler not provided for color-picker');
          return null;
        }
        return (
          <ColorPickerField
            key={key}
            variableName={cp.variable}
            colors={cp.colors}
            selectionTarget={cp.selectionTarget}
            disabled={disableInlineOptions}
            onSelect={onColorOptionSelect}
            className="rich-text-color-picker-element"
            {...(customColor !== undefined ? { frameColor: customColor } : {})}
          />
        );
      }

      case 'color-sample': {
        const cs = element.colorSampleAttributes;
        if (!cs?.variable) {
          console.warn('RichTextRenderer: color-sample element missing variable attribute');
          return null;
        }
        const token = typeof element.content === 'string' ? element.content : '';
        return (
          <ColorSampleBlock
            key={key}
            colorToken={token}
            className="rich-text-color-sample-element"
          />
        );
      }

      case 'code-display': {
        const cd = element.codeDisplayAttributes;
        if (!cd?.variable) {
          console.warn('RichTextRenderer: code-display element missing variable attribute');
          return null;
        }
        const code = typeof element.content === 'string' ? element.content : '';
        return (
          <CodeDisplay key={key} code={code} className="rich-text-code-display-element" />
        );
      }

      case 'time-picker':
        if (!element.timePickerAttributes?.variable) {
          console.warn('RichTextRenderer: time-picker element missing variable attribute');
          return null;
        }
        return (
          <TimePickerField
            key={key}
            variableName={element.timePickerAttributes.variable}
            className="rich-text-time-picker-element"
            ariaLabel="Select time"
          />
        );

      case 'date-picker':
        if (!element.datePickerAttributes?.variable) {
          console.warn('RichTextRenderer: date-picker element missing variable attribute');
          return null;
        }
        return (
          <DatePickerField
            key={key}
            variableName={element.datePickerAttributes.variable}
            className="rich-text-date-picker-element"
            ariaLabel="Select date"
          />
        );

      case 'divider':
        return (
          <hr
            key={key}
            className="rich-text-divider"
            aria-hidden="true"
          />
        );

      case 'activity-list': {
        if (!activityListContext) {
          return null;
        }
        const { onNavigate, onActivityReferenceSelect, onActivityRedo, disableInteractions } =
          activityListContext;
        const rows = (element.activityListRows ?? []) as ActivityListRow[];
        if (!rows.length) {
          return null;
        }
        const selTarget = element.activityListSelectionTarget;
        const selVar = element.activityListSelectionVariable;
        const redoSceneId =
          typeof element.activityListRedoScene === 'string'
            ? element.activityListRedoScene.trim()
            : '';
        const referenceMode =
          !!selTarget &&
          !!selVar &&
          typeof onActivityReferenceSelect === 'function';
        return (
          <ActivityList
            key={key}
            rows={rows}
            disableInteractions={!!disableInteractions}
            showRequirements={element.activityListHideRequirements !== true}
            {...(referenceMode
              ? {
                  referenceSelect: {
                    targetScene: selTarget!,
                    variableName: selVar!,
                    selectionAllowLocked: element.activityListSelectionAllowLocked === true
                  }
                }
              : {})}
            {...(customColor ? { customColor } : {})}
            onRowNavigate={(row) => onNavigate(row.startSceneId)}
            {...(redoSceneId && typeof onActivityRedo === 'function'
              ? { redoTargetScene: redoSceneId, onActivityRedo: (id) => onActivityRedo(redoSceneId, id) }
              : {})}
            {...(referenceMode
              ? {
                  onActivityReferencePick: (activityId: string) => {
                    onActivityReferenceSelect!(activityId, selTarget!, selVar!);
                  }
                }
              : {})}
            className="rich-text-activity-list"
          />
        );
      }

      case 'location-with-portraits':
        if (!element.locationWithPortraitsImageId) {
          console.warn('RichTextRenderer: location-with-portraits element missing imageId');
          return null;
        }
        return (
          <LocationWithPortraits
            key={key}
            imageId={element.locationWithPortraitsImageId}
            portraitIds={element.locationWithPortraitsPortraitIds ?? []}
            alt={element.locationWithPortraitsImageId.replace(/_/g, ' ').replace(/:/g, ' - ')}
            className="rich-text-location-with-portraits"
          />
        );

      case 'character-comment':
        return (
          <CharacterComment
            key={key}
            character={element.character}
            portrait={element.portrait}
            text={element.text}
            timestamp={element.timestamp}
            className="rich-text-character-comment"
            data-testid="character-comment"
          />
        );

      case 'feedback-card':
        if ((element as any).selectionTarget) {
          if (!onFeedbackCardSelect) {
            console.warn('RichTextRenderer: onFeedbackCardSelect handler not provided for selectable feedback cards');
            return null;
          }
          if (!(element as any).feedbackId) {
            console.warn('RichTextRenderer: feedback-card element missing feedbackId for selection');
            return null;
          }
          const buttonStyle: React.CSSProperties = {};
          if (customColor) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
          }
          return (
            <button
              key={key}
              className="feedback-card-option inventory-item-button"
              style={buttonStyle}
              onClick={() => onFeedbackCardSelect((element as any).feedbackId, (element as any).selectionTarget)}
              data-testid="feedback-card-option"
            >
              <FeedbackCard
                type={(element as any).feedbackType}
                title={(element as any).feedbackTitle}
                by={(element as any).feedbackBy}
                at={(element as any).feedbackAt}
                preview={(element as any).feedbackPreview}
                scene={(element as any).feedbackScene}
                deployment={(element as any).feedbackDeployment}
                adventureDisplay={(element as any).feedbackAdventureDisplay}
                className="rich-text-feedback-card"
              />
            </button>
          );
        }
        return (
          <FeedbackCard
            key={key}
            type={(element as any).feedbackType}
            title={(element as any).feedbackTitle}
            by={(element as any).feedbackBy}
            at={(element as any).feedbackAt}
            preview={(element as any).feedbackPreview}
            scene={(element as any).feedbackScene}
            deployment={(element as any).feedbackDeployment}
            adventureDisplay={(element as any).feedbackAdventureDisplay}
            className="rich-text-feedback-card"
          />
        );

      case 'analytics-table-row':
        return (
          <div key={key} className="analytics-table-row" role="row">
            <span className="analytics-table-cell analytics-table-cell-position" role="cell">
              {(element as any).analyticsPosition || '-'}
            </span>
            <span className="analytics-table-cell analytics-table-cell-name" role="cell">
              {(element as any).analyticsName || 'Unknown'}
            </span>
            <span className="analytics-table-cell analytics-table-cell-value" role="cell">
              {(element as any).analyticsValue || '0'}
            </span>
          </div>
        );

      case 'scroll-area': {
        const scrollContent = Array.isArray((element as any).content)
          ? (element as any).content.map((child: RichTextElement, childIndex: number) =>
              renderElement(child, childIndex)
            )
          : [];
        const maxHeight = (element as any).maxHeight;
        const scrollToBottom = (element as any).scrollToBottom === true;
        return (
          <ScrollArea
            key={key}
            maxHeight={maxHeight}
            scrollToBottom={scrollToBottom}
            data-testid="scroll-area"
          >
            {scrollContent}
          </ScrollArea>
        );
      }

      case 'shared-data': {
        const attrs = element.sharedDataAttributes;
        const childElements = Array.isArray(element.content)
          ? (element.content as RichTextElement[])
          : [];
        if (!sessionId) {
          console.warn('RichTextRenderer: sessionId required for shared-data blocks');
          return null;
        }
        return (
          <SharedData
            key={key}
            sessionId={sessionId}
            queries={attrs?.queries ?? []}
            contentElements={childElements}
            className="rich-text-shared-data-embed"
          >
            {(data) => (
              <RichTextRenderer
                description={{
                  elements: data.content !== undefined ? data.content : childElements,
                }}
                {...nestedRendererProps}
              />
            )}
          </SharedData>
        );
      }

      case 'schedule-event':
        if (element.selectionTarget) {
          if (!onScheduleEventSelect) {
            console.warn('RichTextRenderer: onScheduleEventSelect handler not provided for selectable schedule events');
            return null;
          }
          if (!element.scheduleEventId) {
            console.warn('RichTextRenderer: schedule-event element missing id for selection');
            return null;
          }
          const buttonStyle: React.CSSProperties = {};
          if (customColor) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }
          return (
            <button
              key={key}
              className="schedule-event-option inventory-item-button"
              style={buttonStyle}
              onClick={() => onScheduleEventSelect(element.scheduleEventId!, element.selectionTarget!)}
              data-testid="schedule-event-option"
            >
              <ScheduleEventRow
                title={element.scheduleTitle}
                location={element.scheduleLocation}
                locationImageId={element.scheduleLocationImageId}
                startTimeEpochSeconds={element.scheduleStartTimeEpochSeconds}
                durationMinutes={element.scheduleDurationMinutes}
                requiredCharacters={element.scheduleRequiredCharacters}
                requiredPortraits={element.scheduleRequiredPortraits}
                unconfirmedPortraits={element.scheduleUnconfirmedPortraits}
                state={element.scheduleState}
                instruction={element.scheduleInstruction}
                realWorldLocation={element.scheduleRealWorldLocation}
              />
            </button>
          );
        }
        return (
          <ScheduleEventRow
            key={key}
            title={element.scheduleTitle}
            location={element.scheduleLocation}
            locationImageId={element.scheduleLocationImageId}
            startTimeEpochSeconds={element.scheduleStartTimeEpochSeconds}
            durationMinutes={element.scheduleDurationMinutes}
            requiredCharacters={element.scheduleRequiredCharacters}
            requiredPortraits={element.scheduleRequiredPortraits}
            unconfirmedPortraits={element.scheduleUnconfirmedPortraits}
            state={element.scheduleState}
            instruction={element.scheduleInstruction}
            realWorldLocation={element.scheduleRealWorldLocation}
          />
        );

      case 'schedule-event-time': {
        if (element.selectionTarget) {
          if (!onScheduleEventTimeSelect) {
            console.warn('RichTextRenderer: onScheduleEventTimeSelect handler not provided for selectable schedule event times');
            return null;
          }
          if (!element.scheduleEventId || !element.scheduleEventTimeEpochSeconds) {
            console.warn('RichTextRenderer: schedule-event-time element missing id or time');
            return null;
          }
          const buttonStyle: React.CSSProperties = {};
          if (customColor) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }
          const label = formatFriendlyDateTime(element.scheduleEventTimeEpochSeconds) ||
            new Date(element.scheduleEventTimeEpochSeconds * 1000).toLocaleString();
          const acceptedPortraits = Array.isArray(element.scheduleAcceptedPortraits)
            ? element.scheduleAcceptedPortraits
            : [];
          const requiredPortraits = Array.isArray(element.scheduleRequiredPortraits)
            ? element.scheduleRequiredPortraits
            : [];
          return (
            <div key={key} className="schedule-event-time-option-wrapper inline-option-full-width-wrapper">
              <Button
                variant="glass-primary"
                size="md"
                fullWidth
                onClick={() => onScheduleEventTimeSelect(
                  element.scheduleEventId!,
                  element.scheduleEventTimeEpochSeconds!,
                  element.selectionTarget!,
                  element.scheduleRealWorldLocation
                )}
                className="schedule-event-time-option"
                style={buttonStyle}
                data-testid="schedule-event-time-option"
              >
                <div className="schedule-event-time-content">
                  <span className="schedule-event-time-label">{label}</span>
                  {element.scheduleRealWorldLocation?.trim() && (
                    <span
                      className="schedule-event-time-real-world-location"
                      data-testid="schedule-event-time-real-world-location"
                      style={{ fontStyle: 'italic' }}
                    >
                      {element.scheduleRealWorldLocation.trim()}
                    </span>
                  )}
                  {(requiredPortraits.length > 0 || acceptedPortraits.length > 0) && (
                    <div className="schedule-event-time-portraits">
                      {(requiredPortraits.length > 0 ? requiredPortraits : acceptedPortraits).map((portraitId, index) => {
                        const resolvedPortrait = portraitId || 'generic';
                        const isAccepted = acceptedPortraits.includes(resolvedPortrait);
                        return (
                          <PortraitImage
                            key={`${resolvedPortrait}-${index}`}
                            portraitId={resolvedPortrait}
                            variant="thumbnail"
                            alt={`Required character ${index + 1}`}
                            className={isAccepted ? 'schedule-event-time-portrait' : 'schedule-event-time-portrait is-unaccepted'}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </Button>
            </div>
          );
        }
        return null;
      }

      case 'join-event-card': {
        if (!element.scheduleEventId || !element.selectionTarget) {
          return (
            <JoinEventCard
              key={key}
              title={element.scheduleTitle}
              startTimeEpochSeconds={element.scheduleStartTimeEpochSeconds}
            />
          );
        }
        if (!onJoinEventSelect) {
          console.warn('RichTextRenderer: onJoinEventSelect handler not provided for join-event-card');
          return null;
        }
        const buttonStyle: React.CSSProperties = {};
        if (customColor) {
          buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
          buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
        }
        return (
          <button
            key={key}
            className="join-event-card-option inventory-item-button"
            style={buttonStyle}
            onClick={() => onJoinEventSelect(element.scheduleEventId!, element.selectionTarget!)}
            data-testid="join-event-card-option"
          >
            <JoinEventCard
              title={element.scheduleTitle}
              startTimeEpochSeconds={element.scheduleStartTimeEpochSeconds}
            />
          </button>
        );
      }

      case 'schedule-event-attendance':
        return (
          <ScheduleEventAttendance
            key={key}
            requiredPortraits={element.scheduleRequiredPortraits}
            absentPortraits={element.scheduleAbsentPortraits}
          />
        );

      case 'sync-option': {
        const target = element.target ?? (element as { target?: string }).target;
        const hasTarget = target || (element as { syncTargetByCharacter?: Record<string, string> }).syncTargetByCharacter;
        if (hasTarget) {
          if (!onSyncOptionSelect) {
            console.warn('RichTextRenderer: onSyncOptionSelect handler not provided for sync-option');
            return null;
          }
          if (!element.syncVar) {
            console.warn('RichTextRenderer: sync-option element missing syncVar');
            return null;
          }
          const syncTargetByCharacter = (element as { syncTargetByCharacter?: Record<string, string> }).syncTargetByCharacter;
          return (
            <SyncOption
              key={key}
              text={element.syncOptionText ?? 'Ready'}
              syncVar={element.syncVar}
              target={target ?? ''}
              portraits={element.syncPortraits}
              unclickedPortraits={element.syncUnclickedPortraits}
              onSelect={(syncVar, targetScene) =>
                onSyncOptionSelect(syncVar, targetScene, element.syncCharacters, syncTargetByCharacter, (element as { syncOptionEffects?: string }).syncOptionEffects)
              }
              disabled={disableInlineOptions}
              customColor={customColor}
            />
          );
        }
        return null;
      }

      case 'timer-display': {
        const timerEl = element as {
          timerVar?: string;
          timerDisplayType?: string;
          startedAt?: string;
          stoppedAt?: string;
          maxSeconds?: string;
          targetAt?: string;
          armedAt?: string;
          preCountdownSeconds?: string;
          preCountdownAudioId?: string;
        };
        const {
          timerVar,
          timerDisplayType,
          startedAt,
          stoppedAt,
          maxSeconds,
          targetAt,
          armedAt,
          preCountdownSeconds,
          preCountdownAudioId,
        } = timerEl;
        if (!timerVar) {
          console.warn('RichTextRenderer: timer-display element missing timerVar');
          return null;
        }
        const validTypes = ['blind', 'stopwatch', 'countdown', 'min', 'deadline-countdown'];
        const displayType = timerDisplayType as 'blind' | 'stopwatch' | 'countdown' | 'min' | 'deadline-countdown' | undefined;
        const type = displayType && validTypes.includes(displayType) ? displayType : undefined;
        return (
          <TimerDisplay
            key={key}
            timerVar={timerVar}
            displayType={type}
            startedAt={startedAt}
            stoppedAt={stoppedAt}
            maxSeconds={maxSeconds}
            targetAt={targetAt}
            armedAt={armedAt}
            preCountdownSeconds={preCountdownSeconds}
            preCountdownAudioId={preCountdownAudioId}
          />
        );
      }

      case 'timer-audio': {
        const timerEl = element as {
          timerVar?: string;
          startedAt?: string;
          stoppedAt?: string;
          maxSeconds?: string;
          targetAt?: string;
          soundCues?: string;
          armedAt?: string;
          preCountdownSeconds?: string;
          preCountdownAudioId?: string;
        };
        const {
          timerVar,
          startedAt,
          stoppedAt,
          maxSeconds,
          targetAt,
          soundCues,
          armedAt,
          preCountdownSeconds,
          preCountdownAudioId,
        } = timerEl;
        if (!timerVar) {
          console.warn('RichTextRenderer: timer-audio element missing timerVar');
          return null;
        }
        return (
          <TimerAudio
            key={key}
            timerVar={timerVar}
            startedAt={startedAt}
            stoppedAt={stoppedAt}
            maxSeconds={maxSeconds}
            targetAt={targetAt}
            soundCuesJson={soundCues}
            armedAt={armedAt}
            preCountdownSeconds={preCountdownSeconds}
            preCountdownAudioId={preCountdownAudioId}
          />
        );
      }

      case 'conditional': {
        const branch: RichTextElement[] =
          Array.isArray(element.children) && element.children.length > 0
            ? element.children
            : Array.isArray(element.elseChildren)
              ? element.elseChildren
              : [];

        if (branch.length === 0) {
          return null;
        }

        return (
          <React.Fragment key={key}>
            {branch.map((child, childIndex) => renderElement(child, childIndex))}
          </React.Fragment>
        );
      }

      case 'script': {
        const scriptContent = (element as RichTextElement & { content?: RichTextElement[] }).content;
        if (!Array.isArray(scriptContent) || scriptContent.length === 0) {
          return null;
        }
        return (
          <React.Fragment key={key}>
            {scriptContent.map((child, childIndex) => renderElement(child, childIndex))}
          </React.Fragment>
        );
      }

      default:
        // Fallback for unknown element types
        const fallbackContent = Array.isArray(element.content)
          ? element.content.map((child, childIndex) => renderElement(child, childIndex))
          : <SafeText key={key} text={element.content as string} />;
        return fallbackContent;
    }
  };

  // Convert description to RichTextDescription if it's a string
  const richTextDescription = isRichTextDescription(description) 
    ? description 
    : plainTextToRichText(description);

  if (!richTextDescription.elements || richTextDescription.elements.length === 0) {
    return null;
  }

  // Group consecutive top-level portrait elements so they can be wrapped in a centered row
  const isPortraitElement = (el: RichTextElement) =>
    el?.type === 'image' && el?.imageType === 'portrait';
  const isAnalyticsTableRowElement = (el: RichTextElement) =>
    el?.type === 'analytics-table-row';
  const isSpinnerElement = (el: RichTextElement) => el?.type === 'spinner';
  const processedElements: React.ReactNode[] = [];
  let portraitGroup: RichTextElement[] = [];
  let analyticsTableGroup: RichTextElement[] = [];
  let spinnerGroup: RichTextElement[] = [];
  const flushPortraitGroup = (keyPrefix: string) => {
    if (portraitGroup.length > 0) {
      processedElements.push(
        <div
          key={`${keyPrefix}-portrait-row`}
          className="rich-text-portrait-row"
        >
          {portraitGroup.map((el, i) => renderElement(el, i))}
        </div>
      );
      portraitGroup = [];
    }
  };
  const flushAnalyticsTableGroup = (keyPrefix: string) => {
    if (analyticsTableGroup.length > 0) {
      processedElements.push(
        <div
          key={`${keyPrefix}-analytics-table`}
          className="rich-text-analytics-table"
        >
          {analyticsTableGroup.map((el, i) => renderElement(el, i))}
        </div>
      );
      analyticsTableGroup = [];
    }
  };
  const flushSpinnerGroup = (keyPrefix: string) => {
    if (spinnerGroup.length === 0) {
      return;
    }
    if (spinnerGroup.length === 1) {
      const single = spinnerGroup[0];
      if (single) {
        processedElements.push(renderElement(single, 0));
      }
    } else {
      processedElements.push(
        <div
          key={`${keyPrefix}-spinner-row`}
          className="rich-text-spinner-row"
          data-testid="rich-text-spinner-row"
        >
          {spinnerGroup.map((el, i) => renderElement(el, i))}
        </div>
      );
    }
    spinnerGroup = [];
  };
  richTextDescription.elements.forEach((element, index) => {
    if (isPortraitElement(element)) {
      flushAnalyticsTableGroup(`before-${index}`);
      flushSpinnerGroup(`before-${index}`);
      portraitGroup.push(element);
    } else if (isAnalyticsTableRowElement(element)) {
      flushPortraitGroup(`before-${index}`);
      flushSpinnerGroup(`before-${index}`);
      analyticsTableGroup.push(element);
    } else if (isSpinnerElement(element)) {
      flushPortraitGroup(`before-${index}`);
      flushAnalyticsTableGroup(`before-${index}`);
      spinnerGroup.push(element);
    } else {
      flushPortraitGroup(`before-${index}`);
      flushAnalyticsTableGroup(`before-${index}`);
      flushSpinnerGroup(`before-${index}`);
      processedElements.push(renderElement(element, index));
    }
  });
  flushPortraitGroup('end');
  flushAnalyticsTableGroup('end');
  flushSpinnerGroup('end');

  return (
    <div className={`rich-text ${className}`} style={style}>
      {processedElements}
    </div>
  );
};

/**
 * Escapes HTML special characters to prevent XSS
 * @param text - Text to escape
 * @returns Escaped HTML string
 */
const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
    // Note: Removed apostrophe escaping as it causes display issues in React components
};

/**
 * Returns true if the trimmed string looks like a single URL (http/https).
 */
const isStandaloneUrl = (text: string): boolean => {
  const t = typeof text === 'string' ? text.trim() : '';
  return t.length > 10 && (t.startsWith('http://') || t.startsWith('https://')) && !/\s/.test(t);
};

/**
 * Renders a long URL in a readable block with wrapping and a copy button.
 */
const CopyableLinkBlock: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      const t = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(t);
    } catch {
      // fallback for older browsers
      setCopied(false);
      return undefined;
    }
  }, [url]);

  return (
    <span className="rich-text-copyable-link-block" style={{
      display: 'block',
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
      padding: '0.6rem 0.75rem',
      backgroundColor: 'rgba(0,0,0,0.2)',
      borderRadius: '6px',
      fontFamily: 'ui-monospace, monospace',
      fontSize: '0.85rem',
      wordBreak: 'break-all',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    }}>
      <span style={{ userSelect: 'text', cursor: 'text' }}>{url}</span>
      <button
        type="button"
        onClick={handleCopy}
        style={{
          display: 'block',
          marginTop: '0.5rem',
          padding: '0.35rem 0.75rem',
          fontSize: '0.875rem',
          borderRadius: '4px',
          border: '1px solid rgba(255,255,255,0.3)',
          background: 'rgba(255,255,255,0.15)',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </span>
  );
};

/**
 * Renders escaped text so HTML entities (e.g. &amp;) display correctly (e.g. &).
 * Uses dangerouslySetInnerHTML so the browser decodes entities; content is already escaped for safety.
 * If the text is a standalone URL, renders a copyable link block instead for better readability.
 */
const SafeText = ({ text }: { text: string }) => {
  if (isStandaloneUrl(text)) {
    return <CopyableLinkBlock url={text.trim()} />;
  }
  return <span dangerouslySetInnerHTML={{ __html: escapeHtml(text) }} />;
};

export default RichTextRenderer;
